import { useAtomValue } from 'jotai'
import React from 'react'
import { useTranslation } from 'react-i18next'

import {
  buildNineSegmentsTableInfo,
  type NineSegmentColumnKey,
} from '~/apps/ninesegsAnalyzer/utils/standardAnalysis'
import { useHighlightCells } from '~/hooks/useHighlightCells'
import { useSampleSize } from '~/hooks/useSampleSize'
import { type Brand } from '~/hooks/useSurveyQuery'
import { useTamRatio } from '~/hooks/useTamRatio'
import { type RowInfo } from '~/utils'

import {
  dataDisplayTypeAtom,
  hiddenRowIdsAtom,
  nineSegmentHiddenColumnKeysAtom,
  nineSegmentSortValueAtom,
} from '../atoms'

import { useNineSegmentsAnswersArrayData } from './useNineSegmentsAnswersArrayData'
import { useNineSegmentsCustomerAttributesArrayData } from './useNineSegmentsCustomerAttributesArrayData'
import { useNineSegmentsTotalData } from './useNineSegmentsTotalData'

export const useNineSegmentsTableInfo = (
  brand: Brand,
): RowInfo<NineSegmentColumnKey>[] => {
  const { t } = useTranslation()
  const sampleSize = useSampleSize()
  const tamRatio = useTamRatio()

  const dataDisplayType = useAtomValue(dataDisplayTypeAtom)
  const sortValue = useAtomValue(nineSegmentSortValueAtom)
  const hiddenRowIds = useAtomValue(hiddenRowIdsAtom)
  const hiddenColumnKeys = useAtomValue(nineSegmentHiddenColumnKeysAtom)

  const totalData = useNineSegmentsTotalData(brand)
  const answersArrayData = useNineSegmentsAnswersArrayData(brand)
  const customerAttributesArrayData =
    useNineSegmentsCustomerAttributesArrayData(brand)

  const { addHighlightProps } = useHighlightCells(brand.name)

  return React.useMemo(
    () =>
      buildNineSegmentsTableInfo({
        addHighlightProps,
        answersArrayData,
        customerAttributesArrayData,
        dataDisplayType,
        hiddenColumnKeys,
        hiddenRowIds,
        sampleSize,
        sortValue,
        t,
        tamRatio,
        totalData: { counts: totalData.counts, ratios: totalData.ratios },
      }),
    [
      answersArrayData,
      customerAttributesArrayData,
      dataDisplayType,
      hiddenColumnKeys,
      hiddenRowIds,
      sampleSize,
      sortValue,
      t,
      tamRatio,
      addHighlightProps,
      totalData.counts,
      totalData.ratios,
    ],
  )
}
